import { DEFAULT_META } from '../../../shared/constants/categories';
import {
  STATUS_MAP,
  TICKET_STATUS,
} from '../../../shared/constants/statuses';
import { parseValidApiDate } from '../../../shared/utils/format';
import { logger } from '../../../shared/utils/logger';
import {
  getInitials,
  getStringHue,
} from '../../../shared/utils/userPresentation';
import {
  buildCategoryPicMapsByCategory,
  indexUsersById,
  normalizeUserId,
  resolveBackupUser,
} from './ticketAssignmentService';

const PRIORITY_MAP = {
  URGENT: 'urgent',
  CRITICAL: 'urgent',
  HIGH: 'high',
  NORMAL: 'medium',
  MEDIUM: 'medium',
  LOW: 'low',
};

function indexCategories(categories) {
  return (categories || []).reduce((lookup, category) => {
    if (category?.id) lookup[category.id] = category;
    if (category?.mongo_id) lookup[category.mongo_id] = category;
    return lookup;
  }, {});
}

function indexStatuses(statuses) {
  return (statuses || []).reduce((lookup, status) => {
    if (status?.id) lookup[status.id] = status;
    return lookup;
  }, {});
}

function resolveTicketStatus(statusRefId, statusesById) {
  const statusCode = statusesById[statusRefId]?.status_code;
  if (!statusCode) {
    logger.warn('Unresolved status_ref_id, falling back to NEW:', statusRefId);
  }
  return STATUS_MAP[statusCode] || TICKET_STATUS.NEW;
}

function resolveCategory(doc, categoriesById) {
  const categoryRefId = doc.ai_suggested_category_ref_id || doc.category_ref_id || doc.categoryRefId || null;
  const category = categoryRefId ? categoriesById[categoryRefId] || null : null;
  if (categoryRefId && !category) {
    logger.warn('Unresolved category_ref_id; displaying Unclassified:', categoryRefId);
  }
  const rawCategory = doc.category_name || doc.categoryName || doc.category;
  const categoryName = category?.category_name
    || (typeof rawCategory === 'string' ? rawCategory : null)
    || rawCategory?.category_name
    || rawCategory?.label
    || rawCategory?.name
    || null;
  if (!categoryRefId && !categoryName) {
    logger.warn('Ticket has no category classification; displaying Unclassified:', doc.request_no || doc.id || doc._id);
  }
  const meta = category
    ? {
        label: category.label || categoryName,
        icon: category.icon || DEFAULT_META.icon,
        color: category.color || DEFAULT_META.color,
      }
    : categoryName
      ? { ...DEFAULT_META, label: categoryName }
      : { ...DEFAULT_META, label: 'Unclassified' };

  return { categoryRefId, categoryName, meta, category };
}

function resolvePriority(doc) {
  const rawPriority = doc.priority ?? doc.priority_name ?? doc.priority_value ?? '';
  const priorityCode = typeof rawPriority === 'string'
    ? rawPriority.toUpperCase()
    : String(rawPriority?.priority || rawPriority?.name || rawPriority?.label || '').toUpperCase();
  const priority = PRIORITY_MAP[priorityCode];
  if (!priority) {
    logger.warn('Ticket has no recognized priority classification; displaying Unclassified:', doc.request_no || doc.id || doc._id, rawPriority);
    return 'unclassified';
  }
  return priority;
}

export function mapUserToAgent(user) {
  if (!user) return null;

  const name = user.full_name || user.user_name || user.name || user.email || 'Unknown';
  return {
    id: normalizeUserId(user),
    name,
    email: user.email || '',
    initials: getInitials(name),
    hue: getStringHue(name),
    team: user.team_ref_id || '',
  };
}

export function createTicketMappingContext({
  categories,
  statuses,
  users,
  agents = [],
  categoryPicMaps,
}) {
  const usersById = indexUsersById(users);
  const agentsById = indexUsersById(agents);
  return {
    categoriesById: indexCategories(categories),
    statusesById: indexStatuses(statuses),
    usersById: { ...agentsById, ...usersById },
    categoryPicMapsByCategory: buildCategoryPicMapsByCategory(categoryPicMaps),
  };
}

export function mapTicketResponse(doc, context) {
  const { categoryRefId, categoryName, meta, category } = resolveCategory(
    doc,
    context.categoriesById,
  );
  const assignedPicUserRefId = normalizeUserId(doc.assigned_pic_user_ref_id)
    || normalizeUserId(doc.assignee?.id ?? doc.assignee?._id);
  const existingAssigneeId = normalizeUserId(doc.assignee?.id ?? doc.assignee?._id);
  const assignedUser = assignedPicUserRefId
    ? context.usersById[assignedPicUserRefId]
      || (existingAssigneeId === assignedPicUserRefId ? doc.assignee : null)
    : null;
  if (assignedPicUserRefId && !context.usersById[assignedPicUserRefId] && !assignedUser) {
    logger.warn('Unresolved assigned_pic_user_ref_id, preserving assigned identity:', assignedPicUserRefId);
  }
  const hasBackupPicSnapshot = Object.prototype.hasOwnProperty.call(
    doc,
    'backup_pic_user_ref_id',
  );
  const backupUser = hasBackupPicSnapshot
    ? context.usersById[normalizeUserId(doc.backup_pic_user_ref_id)] || null
    : resolveBackupUser(
        { categoryRefId, mainPicId: assignedPicUserRefId },
        context.categoryPicMapsByCategory,
        context.usersById,
      );
  if (doc.backup_pic_user_ref_id && !backupUser) {
    logger.warn('Unresolved backup_pic_user_ref_id, falling back to unassigned:', doc.backup_pic_user_ref_id);
  }
  const categoryKey = categoryName || (category ? categoryRefId : 'unclassified');
  const created = parseValidApiDate(doc.created_at);
  const status = resolveTicketStatus(doc.status_ref_id, context.statusesById);
  const updated = parseValidApiDate(doc.updated_at);
  const endDate = parseValidApiDate(doc.end_date)
    || (status === TICKET_STATUS.CLOSED ? updated : null);

  return {
    id: doc.request_no || doc.id,
    _id: doc.id,
    title: doc.subject || '(no subject)',
    type: categoryKey,
    typeMeta: { label: categoryName || meta.label, icon: meta.icon, color: meta.color },
    category: categoryKey,
    categoryRefId,
    assigned_pic_user_ref_id: assignedPicUserRefId,
    status,
    priority: resolvePriority(doc),
    requester: {
      name: doc.requester_name || doc.requester_email || 'Unknown',
      email: doc.requester_email || '',
      dept: assignedUser?.team_ref_id || '',
    },
    customers: doc.customers ?? '',
    cc_recipients: doc.cc_recipients || [],
    assignee: mapUserToAgent(assignedUser) || (assignedPicUserRefId ? { id: assignedPicUserRefId } : null),
    backupAssignee: mapUserToAgent(backupUser),
    created,
    updated,
    updatedBy:
      doc.updated_by ||
      doc.updated_by_user_ref_id ||
      doc.changed_by_user_ref_id ||
      null,
    startDate: parseValidApiDate(doc.start_date),
    endDate,
    resolutionTime: doc.resolution_time || null,
    slaPolicyRefId: doc.sla_policy_ref_id || null,
    slaProcessingTime: doc.processing_time ?? null,
    slaProcessingUnit: doc.processing_unit || null,
    dueAt: parseValidApiDate(doc.sla_due_at || doc.due_at || null),
    description: doc.description || '',
    confidence: doc.confidence_score || 0,
    reasoning: doc.reasoning || '',
    emailCount: doc.email_count || 0,
  };
}

export function mapTicketResponses(ticketDocs, context) {
  return (ticketDocs || []).map(doc => mapTicketResponse(doc, context));
}

export function buildAgents(tickets, users) {
  const loadByUserId = (tickets || []).reduce((lookup, ticket) => {
    const userId = ticket.assignee?.id;
    if (userId) lookup[userId] = (lookup[userId] || 0) + 1;
    return lookup;
  }, {});

  return (users || [])
    .filter(user => user.is_active !== false)
    .map(user => ({
      ...mapUserToAgent(user),
      load: loadByUserId[user.id] || 0,
    }))
    .sort((first, second) => second.load - first.load);
}
